import { useMutation, useQuery } from "@tanstack/react-query";
import { CounterSchemaT } from "../types/schema/counterSchema";
import axiosInstance from "./axios";
import {
  AutoClosedSessionItem,
  AutoClosedSessionReconcilePayload,
  AutoClosedSessionsResponse,
  Counter,
  CounterFilterParams,
  CounterOpeningBalanceResponse,
} from "../types/counterTypes";
import { PaginationResponse } from "../types/commonTypes";
import {
  PosSettlementSessionParams,
  PosSettlementSessionsResponse,
  PosSettlementTransactionPayload,
} from "../types/posSettlementTypes";

export const useAddCounter = () => {
  return useMutation({
    mutationKey: ["useAddCounter"],
    mutationFn: async (postData: CounterSchemaT) => {
      const { data } = await axiosInstance.post("/counter", postData);
      return data;
    },
  });
};

export const useEditCounter = () => {
  return useMutation({
    mutationKey: ["useEditCounter"],
    mutationFn: async ({ counterId, postData }: { counterId: number; postData: CounterSchemaT }) => {
      const { data } = await axiosInstance.put(`/counter`, { ...postData, counterId });
      return data;
    },
  });
};

export const useGetAllCounters = ({
  params,
}: {
  params: CounterFilterParams;
}) => {
  return useQuery({
    queryKey: ["useGetAllCounters", params],
    queryFn: async () => {
      const { data } = await axiosInstance.get("/counter/get-all", {
        params,
      });
      return data as PaginationResponse<Counter>;
    },
    enabled: !!params && !!params.branchId,
  });
};

export const useGetCounterSummary = ({
  branchId,
  counterId,
}: {
  branchId?: string | number;
  counterId?: string | number;
}) => {
  return useQuery({
    queryKey: ["useGetCounterSummary", branchId, counterId],
    queryFn: async () => {
      const { data } = await axiosInstance.get(
        `/counter/counter/${branchId}/${counterId}/summary`,
      );
      return data as {
        counterId: number;
        counterName: string;
        expectedCash: number;
        expectedBank: number;
        openingCash: number;
        openingBank: number;
        openingBalance: number;
        businessDate: string;
        status: "OPEN" | string;
        activeStaff: string[];
      };
    },
    enabled: !!branchId && !!counterId,
    retry: false, // don't retry on 404
  });
};

export const useGetCounterOpeningBalance = ({
  counterId,
  branchId,
}: {
  counterId?: string | number;
  branchId?: string | number;
}) => {
  return useQuery({
    queryKey: ["useGetCounterOpeningBalance", counterId],
    queryFn: async () => {
      const { data } = await axiosInstance.get(
        `/counter/counter/${branchId}/${counterId}/opening-balance`,
      );
      return data as CounterOpeningBalanceResponse;
    },
    enabled: !!counterId,
  });
};

export const usePostCounterOpen = () => {
  return useMutation({
    mutationKey: ["usePostCounterOpen"],
    mutationFn: async ({
      counterId,
      openingCash,
      openingBank,
      branchId,
    }: {
      counterId: number;
      openingCash: number;
      openingBank: number;
      branchId?: string | number;
    }) => {
      const { data } = await axiosInstance.post(`/counter/${counterId}/open`, {
        openingCash,
        openingBank,
        branchId,
      });
      return data;
    },
  });
};

export const usePostCounterClose = () => {
  return useMutation({
    mutationFn: async ({
      counterId,
      closingCash,
      closingBank,
      remarks,
      varianceReason,
      branchId,
    }: {
      counterId: number;
      closingCash: number;
      closingBank: number;
      remarks: string;
      varianceReason: string;
      branchId?: string | number;
    }) => {
      const { data } = await axiosInstance.post(`/counter/${counterId}/close`, {
        closingCash,
        closingBank,
        remarks,
        varianceReason,
        branchId,
      });
      return data;
    },
  });
};

export const useGetAutoClosedSessions = ({
  branchId,
  businessDate,
  page,
  size,
}: {
  branchId?: number | string;
  businessDate?: string;
  page?: number;
  size?: number;
}) => {
  return useQuery({
    queryKey: ["useGetAutoClosedSessions", branchId, businessDate, page, size],
    queryFn: async () => {
      const { data } = await axiosInstance.get("/counter/auto-closed-sessions", {
        params: {
          branchId,
          businessDate,
          page,
          size,
        },
      });
      return data as AutoClosedSessionsResponse;
    },
    enabled: !!branchId && !!businessDate,
  });
};

export const useReconcileAutoClosedSession = () => {
  return useMutation({
    mutationKey: ["useReconcileAutoClosedSession"],
    mutationFn: async ({
      sessionId,
      payload,
    }: {
      sessionId: number;
      payload: AutoClosedSessionReconcilePayload;
    }) => {
      const { data } = await axiosInstance.put(
        `/counter/sessions/${sessionId}/reconcile`,
        payload,
      );
      return data as AutoClosedSessionItem;
    },
  });
};

export const useGetPosSettlementSessions = ({
  params,
}: {
  params: PosSettlementSessionParams;
}) => {
  return useQuery({
    queryKey: ["useGetPosSettlementSessions", params],
    queryFn: async () => {
      const { data } = await axiosInstance.get("/counter/pos-all", {
        params,
      });
      return data as PosSettlementSessionsResponse;
    },
    enabled: !!params.branchId,
  });
};

export const usePostPosSettlement = () => {
  return useMutation({
    mutationKey: ["usePostPosSettlement"],
    mutationFn: async (postData: PosSettlementTransactionPayload) => {
      const { data } = await axiosInstance.post(
        "/bank-transactions/pos-settle",
        postData,
      );
      return data;
    },
  });
};
